import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  TextField,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { enqueueSnackbar } from "notistack";
import { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import * as Yup from "yup";
import { createProject, getManagedAccounts } from "../../api/account";
import { useTranslationHook } from "../../hooks";
import { useFormStyles } from "../../hooks/useFormStyles";
import { setLoading } from "../../store/UI";
import { CreateProjectRequest } from "../../types/interfaces/project";
import { Account } from "../../types/model/account";
import { StoreInterfaces } from "../../types/store";

interface ProjectQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (projectId: number) => void;
  shouldNavigate?: boolean;
}

const ProjectQuickCreateDialog: FC<ProjectQuickCreateDialogProps> = ({
  open,
  onClose,
  onSuccess,
  shouldNavigate
}): ReactElement => {
  const { t } = useTranslationHook();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formStyles = useFormStyles();
  const session = useSelector((state: StoreInterfaces) => state.session);

  const ownerId = session?.userId;

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    const fetchAccounts = async () => {
      setAccountsLoading(true);
      try {
        const response = await getManagedAccounts();
        setAccounts(response);
      } catch (error) {
        console.error(error);
        enqueueSnackbar(t("error.data-not-loaded"), { variant: "error" });
      } finally {
        setAccountsLoading(false);
      }
    };

    fetchAccounts();
  }, [open]);

  const handleSubmit = async (values: any) => {
    dispatch(setLoading(true));

    const request: CreateProjectRequest = {
      accountId: values.account.id,
      name: values.name,
      description: values.description,
      lat: values.lat ? Number.parseFloat(values.lat) : 0,
      lng: values.lng ? Number.parseFloat(values.lng) : 0,
      ownerId: ownerId,
      ...(values.startDate ? { startDate: values.startDate } : {}),
      ...(values.endDate ? { endDate: values.endDate } : {}),
    };

    try {
      const response = await createProject(values.account.id, request);
      enqueueSnackbar(t("projects.created"), { variant: "success" });

      if (shouldNavigate) {
        navigate(
          `/accounts/${values.account.id}/projects/${response.id}`,
          { state: { fromAdd: true } }
        );
      }

      if (onSuccess) {
        onSuccess(response.id);
      }

      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t("projects.create")}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Formik
          initialValues={{
            account: null,
            name: "",
            description: "",
            lat: "",
            lng: "",
            startDate: "",
            endDate: "",
          }}
          onSubmit={handleSubmit}
          validationSchema={Yup.object().shape({
            account: Yup.object()
              .nullable()
              .required(t("projects.account-required")),
            name: Yup.string().required(
              t("admin.projects.project-name-required")
            ),
            description: Yup.string().required(
              t("admin.projects.project-description-required")
            ),
            lat: Yup.number()
              .typeError("Must be a number")
              .min(-90, t("admin.projects.project-valid-lat"))
              .max(90, t("admin.projects.project-valid-lat")),
            lng: Yup.number()
              .typeError("Must be a number")
              .min(-180, t("admin.projects.project-valid-lng"))
              .max(180, t("admin.projects.project-valid-lng")),
          })}
        >
          {(props) => {
            const {
              values,
              touched,
              errors,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
              isSubmitting,
            } = props;

            return (
              <Box component="form" onSubmit={handleSubmit}>
                <Grid2 container spacing={2}>
                  <Grid2 size={12}>
                    <Autocomplete
                      sx={{ pt: 1 }}
                      options={accounts}
                      loading={accountsLoading}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      value={values.account}
                      onChange={(_, newValue) =>
                        setFieldValue("account", newValue)
                      }
                      onBlur={handleBlur}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          id="account"
                          label={t("projects.select-account")}
                          error={!!(errors.account && touched.account)}
                          helperText={
                            errors.account && touched.account
                              ? (errors.account)
                              : ""
                          }
                          sx={{ ...formStyles }}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={12}>
                    <TextField
                      fullWidth
                      required
                      id="name"
                      label={t("admin.projects.name")}
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.name && touched.name)}
                      helperText={errors.name ? errors.name : ""}
                      sx={{ ...formStyles }}
                    />
                  </Grid2>

                  <Grid2 size={12}>
                    <TextField
                      fullWidth
                      required
                      id="description"
                      label={t("admin.projects.description")}
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.description && touched.description)}
                      helperText={
                        errors.description ? errors.description : ""
                      }
                      multiline
                      rows={3}
                      sx={{ ...formStyles }}
                    />
                  </Grid2>

                  <Grid2 size={12}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      sx={{ fontWeight: 600, mb: 1, mt: 3 }}
                    >
                      {t("projects.location")} ({t("generic.optional")})
                    </Typography>
                  </Grid2>

                  <Grid2 size={6}>
                    <TextField
                      fullWidth
                      id="lat"
                      label={t("admin.projects.lat")}
                      value={values.lat}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.lat && touched.lat)}
                      helperText={touched.lat && errors.lat}
                      sx={formStyles}
                    />
                  </Grid2>

                  <Grid2 size={6}>
                    <TextField
                      fullWidth
                      id="lng"
                      label={t("admin.projects.lng")}
                      value={values.lng}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.lng && touched.lng)}
                      helperText={touched.lng && errors.lng}
                      sx={formStyles}
                    />
                  </Grid2>

                  <Grid2 size={12}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      {t("projects.dates")} ({t("generic.optional")})
                    </Typography>
                  </Grid2>

                  <Grid2 size={6}>
                    <TextField
                      fullWidth
                      id="startDate"
                      type="date"
                      label={t("admin.projects.start-date")}
                      InputLabelProps={{ shrink: true }}
                      value={values.startDate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.startDate && touched.startDate)}
                      helperText={touched.startDate && errors.startDate}
                      sx={formStyles}
                    />
                  </Grid2>

                  <Grid2 size={6}>
                    <TextField
                      fullWidth
                      id="endDate"
                      type="date"
                      label={t("admin.projects.end-date")}
                      InputLabelProps={{ shrink: true }}
                      value={values.endDate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(errors.endDate && touched.endDate)}
                      helperText={touched.endDate && errors.endDate}
                      sx={formStyles}
                    />
                  </Grid2>
                </Grid2>

                <DialogActions sx={{ mt: 3 }}>
                  <Button onClick={onClose} color="secondary">{t("generic.cancel")}</Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    disabled={isSubmitting}
                  >
                    {t("generic.create")}
                  </Button>
                </DialogActions>
              </Box>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectQuickCreateDialog;